import React, { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { DatabasesAPI } from '@makyoapp/makyo-sdk'
import { Loading } from '@makyoapp/makyo-ui'

import { queryKeys } from '@/lib/react-query'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useSingleProjectContext } from '@/context/SingleProjectContext'
import { useIsProjectDb } from '@/hooks/useIsProjectDb'
import { useParamId } from '@/hooks/useParamId'
import { ORG_DATABASE_ROUTE, ORG_ROUTE, PROJECT_ROUTE } from '@/shared/constants'
import { GridV2Provider } from '@/components/GridV2/contexts/GridV2Context'
import { GridV2 } from '@/components/GridV2/GridV2'
import Layout from '@/components/Layout'
import { PageNotFound } from '@/components/PageNotFound'

import { CreateTableModal } from './components/CreateTableModal'
import { CreateTableOptionsModal } from './components/CreateTableOptionsModal'
import { DeleteTableModal } from './components/DeleteTableModal'
import { QueryBuilderModal } from './components/QueryBuilderModal'
import { SnapshotBuilderModal } from './components/SnapshotBuilderModal'
import LayoutHeaderLeftSection from './LayoutHeaderLeftSection'
import LayoutHeaderRightSection from './LayoutHeaderRightSection'

/**
 * New Database table detail page (Grid V2)
 *
 * @todo
 * - Create table modal
 * - Delete table modal
 */
const DbTableGrid = ({ dbID, orgID }: { dbID: string; orgID: string }) => {
  const queryClient = useQueryClient()

  const { project } = useSingleProjectContext()
  const isProject = useIsProjectDb()

  const { currentOrg } = useCurrentOrgContext()
  const { dbClient, showSnapshotTable, showQueryTable } = useDbClientContext()

  const location = useLocation()
  const isDbRoute = location.pathname.startsWith(ORG_ROUTE) && location.pathname.includes(ORG_DATABASE_ROUTE)

  /**
   * Update last updated time for the database
   */
  useEffect(() => {
    if (isDbRoute) {
      DatabasesAPI.ReqUpdateAccessTime(dbID).then(() => {
        queryClient.invalidateQueries({ queryKey: [queryKeys.orgDatabases, orgID] })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tbId = useParamId('tbl') as string
  const table = dbClient?.tablesMap.get(tbId)

  if (!table) {
    // if no table, show 404 page
    if (dbClient?.tablesMap.size === 0) {
      return <PageNotFound />
    }

    // redirect to first table
    const firstTableId = dbClient?.GetTables()[0]?.id
    return (
      <Navigate
        to={
          isProject
            ? `${PROJECT_ROUTE}/${project?.id}/${ORG_DATABASE_ROUTE}/${firstTableId}`
            : `${ORG_ROUTE}/${currentOrg?.id}/${ORG_DATABASE_ROUTE}/${dbID}/${firstTableId}`
        }
        replace={true}
      />
    )
  }

  return (
    <GridV2Provider>
      <Layout
        title={<LayoutHeaderLeftSection />}
        actions={<LayoutHeaderRightSection />}
        noPadding
        headerNoDefaultClassName
        wrapClassName='relative mk-ag-wrap'
      >
        <React.Suspense fallback={<Loading />}>
          <GridV2 />
        </React.Suspense>

        <DeleteTableModal />
        <CreateTableModal />
        <CreateTableOptionsModal />

        {showSnapshotTable && <SnapshotBuilderModal />}
        {showQueryTable && <QueryBuilderModal />}
      </Layout>
    </GridV2Provider>
  )
}

export default DbTableGrid
